import { useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Printer } from 'lucide-react'
import { useLmsPrint } from '@/hooks/useLmsPrint'
import { LmsPdfDownloadButton } from '@/components/admin/print/LmsPdfDownloadButton'
import { KatexText } from '@quebon/shared-learning'
import { useLearnerToday } from '@/hooks/coaching/useLearnerToday'
import { useReviewCandidates, useTestReport } from '@/hooks/coaching/useTests'
import { useDesignatedReviews } from '@/hooks/coaching/useDesignatedReview'
import type { LcTestReportItemView } from '@/types/coaching'
import { ReviewPlacementDialog } from '@/components/admin/coaching/ReviewPlacementDialog'

const THRESHOLDS = [60, 70, 80]

function parsePositiveInt(value: string | undefined): number | null {
  if (!value) return null
  const n = Number(value)
  return Number.isFinite(n) && n > 0 && Number.isInteger(n) ? n : null
}

function formatPrintDate(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}.${p(d.getMonth() + 1)}.${p(d.getDate())}`
}

/**
 * 챕터/누적 테스트 리포트 A4 인쇄·PDF 페이지 (Phase 5c, plan §6 "모달 아닌 별도 A4 인쇄").
 * 문항별 정오답/단원/해설 + 점수 + 복습추출(기준 정답률 → 기준 이하 레슨 → 교사 [복습 지정]).
 * 누적 수학머리는 BIGGO 임베디드 평가의 공식 결과 요약으로 대체됨(ADR 0009, CumulativeAssessmentReportPage) — 이 출력물에는 포함하지 않는다.
 */
export default function TestReportPrintPage() {
  const { t } = useTranslation('coaching')
  const { learnerId: learnerIdRaw, testId: testIdRaw } = useParams<{ learnerId: string; testId: string }>()
  const learnerId = parsePositiveInt(learnerIdRaw)
  const testId = parsePositiveInt(testIdRaw)

  if (learnerId === null || testId === null) {
    return (
      <div className="p-8 text-red-600" role="alert">
        {t('report.invalidParams')}
      </div>
    )
  }
  return <ReportContent learnerId={learnerId} testId={testId} />
}

function ResultBadge({ value }: { value: boolean | null }) {
  const { t } = useTranslation('coaching')
  if (value === true) return <span className="font-bold text-green-700">O</span>
  if (value === false) return <span className="font-bold text-red-600">X</span>
  return <span className="text-gray-400">{t('report.result.ungraded')}</span>
}

function ReportContent({ learnerId, testId }: { learnerId: number; testId: number }) {
  const { t } = useTranslation(['coaching', 'common'])
  const learnerQ = useLearnerToday(learnerId)
  const reportQ = useTestReport(learnerId, testId)
  const [threshold, setThreshold] = useState(70)
  const candidatesQ = useReviewCandidates(learnerId, testId, threshold)
  const reviewsQ = useDesignatedReviews(learnerId)
  const [placementCandidate, setPlacementCandidate] = useState<{ lessonId: number; title: string } | null>(null)
  const isDesignated = (lessonId: number) =>
    (reviewsQ.data ?? []).some((r) => r.reviewLessonId === lessonId)

  const printRef = useRef<HTMLDivElement>(null)
  const { print: reactToPrintFn, downloadPdf, canDownloadPdf, isDownloading, error: pdfError } = useLmsPrint({
    contentRef: printRef,
    canPrint: !!reportQ.data && !reportQ.isFetching && !learnerQ.isFetching && !reportQ.error && !learnerQ.error,
    pageStyle: `@page { size: A4 portrait; margin: 14mm; } @media print { .print-action-bar { display: none !important; } }`,
  })

  if (reportQ.isLoading) {
    return (
      <div className="p-8" role="status" aria-live="polite">
        {t('report.loading')}
      </div>
    )
  }
  if (reportQ.error || !reportQ.data) {
    return (
      <div className="p-8 text-red-600" role="alert">
        {t('report.error')}
      </div>
    )
  }

  const report = reportQ.data
  const learnerName = learnerQ.data?.learner.fullName ?? ''
  const title = t('test.chapterTitle', { chapter: report.chapterOrdinal })

  return (
    <div className="min-h-screen bg-gray-100">
      {/* 액션 바 + 복습추출(인쇄 제외) */}
      <div className="print-action-bar sticky top-0 z-10 space-y-3 border-b border-gray-200 bg-white px-6 py-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => reactToPrintFn()}
            className="inline-flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white hover:bg-primary-600"
          >
            <Printer size={16} />
            {t('report.print')}
          </button>
          {canDownloadPdf && (
            <LmsPdfDownloadButton
              onClick={downloadPdf}
              disabled={reportQ.isFetching || learnerQ.isFetching || !!learnerQ.error}
              isDownloading={isDownloading}
              error={pdfError}
            />
          )}
        </div>

          <section data-testid="review-extract" className="rounded-lg border border-gray-200 p-3">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-gray-800">{t('report.review.title')}</span>
            <label className="text-xs text-gray-500">
              {t('report.review.threshold')}{' '}
              <select
                data-testid="review-threshold"
                value={threshold}
                onChange={(e) => setThreshold(Number(e.target.value))}
                className="rounded border border-gray-300 px-1.5 py-0.5 text-xs"
              >
                {THRESHOLDS.map((th) => (
                  <option key={th} value={th}>
                    {th}%
                  </option>
                ))}
              </select>
            </label>
          </div>
          {candidatesQ.isLoading ? (
            <p className="text-xs text-gray-400">{t('report.loading')}</p>
          ) : (candidatesQ.data?.length ?? 0) === 0 ? (
            <p className="text-xs text-gray-400">{t('report.review.empty')}</p>
          ) : (
            <ul className="space-y-1">
              {candidatesQ.data!.map((c) => (
                <li
                  key={c.lessonId}
                  data-testid={`review-candidate-${c.lessonId}`}
                  className="flex flex-wrap items-center gap-2 text-sm"
                >
                  <span className="text-gray-800">{c.title}</span>
                  {c.correctRate != null && (
                    <span className="text-xs text-gray-500">
                      {t('library.lessonCorrectRate', { rate: c.correctRate })}
                    </span>
                  )}
                  {isDesignated(c.lessonId) ? (
                    <span className="text-xs font-medium text-lc-mint-700">{t('report.review.designated')}</span>
                  ) : (
                    <button
                      type="button"
                      data-testid={`designate-${c.lessonId}`}
                      onClick={() => setPlacementCandidate({ lessonId: c.lessonId, title: c.title })}
                      className="ml-auto rounded border border-primary-200 px-2 py-0.5 text-xs font-medium text-primary-700 hover:bg-primary-50 disabled:opacity-50"
                    >
                      {t('report.review.designate')}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
          </section>
          {placementCandidate && (
            <ReviewPlacementDialog
              learnerId={learnerId}
              reviewLessonId={placementCandidate.lessonId}
              reviewLessonTitle={placementCandidate.title}
              onClose={() => setPlacementCandidate(null)}
            />
          )}
      </div>

      {/* 인쇄 본문 (A4) */}
      <div className="mx-auto py-8" style={{ maxWidth: 794 }}>
        <div ref={printRef} className="bg-white p-10 shadow-lg print:shadow-none" data-testid="test-report-print">
          <header className="mb-4 border-b-2 border-gray-800 pb-2">
            <h1 className="text-lg font-bold text-gray-900">{t('report.heading', { title })}</h1>
            <div className="mt-1 flex flex-wrap gap-4 text-sm text-gray-600">
              <span>{t('report.learner', { name: learnerName })}</span>
              <span>{t('report.date', { date: formatPrintDate(new Date()) })}</span>
              {report.score != null && (
                <span className="font-semibold text-gray-900">{t('report.score', { score: report.score })}</span>
              )}
            </div>
          </header>

          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-gray-300 text-left text-xs text-gray-500">
                <th className="w-10 py-1">{t('report.col.number')}</th>
                <th className="w-12 py-1 text-center">{t('report.col.result')}</th>
                <th className="py-1">{t('report.col.unit')}</th>
                <th className="w-16 py-1">{t('report.col.type')}</th>
              </tr>
            </thead>
            <tbody>
              {report.items.map((item) => (
                <ReportRow key={item.quizId} item={item} />
              ))}
            </tbody>
          </table>

        </div>
      </div>
    </div>
  )
}

function ReportRow({ item }: { item: LcTestReportItemView }) {
  const { t } = useTranslation('coaching')
  const snapshot = item.questionSnapshot
  const questionText = snapshot && typeof snapshot === 'object'
    ? [snapshot.title, snapshot.problem].filter((value): value is string => typeof value === 'string' && value.length > 0).join(' ')
    : ''
  return (
    <tr className="border-b border-gray-100 align-top">
      <td className="py-1.5 text-gray-700">{item.number}</td>
      <td className="py-1.5 text-center">
        <ResultBadge value={item.gradedIsCorrect} />
      </td>
      <td className="py-1.5 text-gray-800">
        {questionText && <div className="mb-0.5 font-medium"><KatexText text={questionText} /></div>}
        {item.originLessonTitle ?? <span className="text-gray-300">—</span>}
        {item.explanation && <div className="mt-0.5 text-xs text-gray-500 whitespace-pre-wrap"><KatexText text={item.explanation} /></div>}
      </td>
      <td className="py-1.5 text-xs text-gray-500">{t(`report.source.${item.source}`)}</td>
    </tr>
  )
}
